import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { 
  FiFolder, 
  FiFileText, 
  FiBookmark, 
  FiSettings, 
  FiMail, 
  FiEdit3, 
  FiUsers,
  FiUserCheck, 
  FiShield, 
  FiDatabase,
  FiBarChart2,
  FiGlobe,
  FiMonitor,
  FiActivity,
  FiLayers,
  FiGrid,
  FiMenu,
  FiX,
  FiImage,
  FiKey,
  FiList,
  FiClock,
  FiServer
} from 'react-icons/fi';
import config from '../config/frontend.config';
import { usePermissions } from '../contexts/PermissionContext';
import { useMobileMenu } from '../contexts/MobileMenuContext';
import MenuOrderManager from './MenuOrderManager';
import axiosInstance from '../AxiosInstance';
import './Sidebar.css';

// Global flag to prevent duplicate menu preferences calls
let globalMenuPreferencesFetching = false;

function RoleBasedSidebar() {
  const { userRole, hasPermission, isRole, permissions } = usePermissions();
  const { isMobileMenuOpen, closeMobileMenu } = useMobileMenu();
  const isSuperAdmin = isRole('SuperAdmin');
  const isAdmin = isRole('Admin');
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showMenuOrderManager, setShowMenuOrderManager] = useState(false);
  const [customMenuOrder, setCustomMenuOrder] = useState([]);
  const [loadingPreferences, setLoadingPreferences] = useState(true); // Start as true to show shimmer initially

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        closeMobileMenu();
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, [closeMobileMenu]);

  // Fetch user menu preferences
  useEffect(() => {
    const fetchMenuPreferences = async () => {
      // Global check to prevent duplicate calls
      if (globalMenuPreferencesFetching) {
        return;
      }

      try {
        globalMenuPreferencesFetching = true;
        setLoadingPreferences(true);
        const userId = config.getUserId();
        
        if (!userId) return;

        // Fetch menu preferences directly from API (source of truth)
        const response = await axiosInstance.get(
          `${config.api.baseURL}/${config.api.apiPath}/user/menu-preferences/${userId}`
        );

        if (response.data.statusCode === 200) {
          const serverOrder = response.data.data.menuOrder || [];
          setCustomMenuOrder(serverOrder);
        }
      } catch (error) {
        console.error('Error fetching menu preferences:', error);
        // Set default empty order if server fails
        setCustomMenuOrder([]);
      } finally {
        setLoadingPreferences(false);
        globalMenuPreferencesFetching = false; // Reset global flag
      }
    };

    fetchMenuPreferences();
  }, []);

  // Superadmin menu items (all permissions)
  const getSuperAdminMenuItems = () => {
    const items = [
      {
        path: '/',
        icon: <FiGrid className="sidebar-icon" />,
        text: 'Dashboard',
        end: true
      },
      {
        path: '/projects',
        icon: <FiFolder className="sidebar-icon" />,
        text: 'Projects'
      },
      {
        path: '/monitoring',
        icon: <FiMonitor className="sidebar-icon" />,
        text: 'Monitoring'
      },
      // {
      //   path: '/admin-panel',
      //   icon: <FiShield className="sidebar-icon" />,
      //   text: 'Admin Panel'
      // },
      {
        path: '/role-management',
        icon: <FiKey className="sidebar-icon" />,
        text: 'Role Management'
      },
      {
        path: '/user-role-assignment',
        icon: <FiUsers className="sidebar-icon" />,
        text: 'Assign Roles'
      },
      // {
      //   path: '/system-settings',
      //   icon: <FiSettings className="sidebar-icon" />,
      //   text: 'System Settings'
      // },
      {
        path: '/cdn',
        icon: <FiImage className="sidebar-icon" />,
        text: 'Image CDN'
      },
      // {
      //   path: '/all-users',
      //   icon: <FiUsers className="sidebar-icon" />,
      //   text: 'All Users'
      // },
      // {
      //   path: '/all-teams',
      //   icon: <FiGlobe className="sidebar-icon" />,
      //   text: 'All Teams'
      // },
      // {
      //   path: '/system-activity',
      //   icon: <FiBarChart2 className="sidebar-icon" />,
      //   text: 'System Activity'
      // },
      // {
      //   path: '/database',
      //   icon: <FiDatabase className="sidebar-icon" />,
      //   text: 'Database'
      // },
      // {
      //   path: '/monitoring',
      //   icon: <FiMonitor className="sidebar-icon" />,
      //   text: 'Monitoring'
      // },
      {
        path: '/activity',
        icon: <FiActivity className="sidebar-icon" />,
        text: 'Activity'
      },
      {
        path: '/library',
        icon: <FiBookmark className="sidebar-icon" />,
        text: 'Library'
      },
      {
        path: '/invitations',
        icon: <FiUsers className="sidebar-icon" />,
        text: 'Invitations'
      },
      {
        path: '/team-members',
        icon: <FiUserCheck className="sidebar-icon" />,
        text: 'Team Members'
      },
      {
        path: '/settings',
        icon: <FiSettings className="sidebar-icon" />,
        text: 'Settings'
      },
      // {
      //   path: '/email-builder',
      //   icon: <FiEdit3 className="sidebar-icon" />,
      //   text: 'Email Builder'
      // },
      // {
      //   path: '/modern-email-builder',
      //   icon: <FiEdit3 className="sidebar-icon" />,
      //   text: 'Modern Email Builder'
      // },
      // {
      //   path: '/advanced-email-builder',
      //   icon: <FiLayers className="sidebar-icon" />,
      //   text: 'Advanced Email Builder'
      // },
      // {
      //   path: '/ultra-modern-email-builder',
      //   icon: <FiGrid className="sidebar-icon" />,
      //   text: 'Ultra Modern Builder'
      // },
      // {
      //   path: '/new-builderjs',
      //   icon: <FiLayers className="sidebar-icon" />,
      //   text: 'New BuilderJS'
      // },
      {
        path: '/builderjs6',
        icon: <FiEdit3 className="sidebar-icon" />,
        text: 'BuilderJS6'
      },
      {
        path: '/cronjobs',
        icon: <FiClock className="sidebar-icon" />,
        text: 'Scheduled Emails'
      },

    ];

    // Add Email Campaigns only if user has permission to view email campaigns
    if (hasPermission('canViewEmailCampaigns')) {
      items.push({
        path: '/email-campaigns',
        icon: <FiMail className="sidebar-icon" />,
        text: 'Email Campaigns'
      });
      items.push({
        path: '/email-batches',
        icon: <FiUsers className="sidebar-icon" />,
        text: 'Email Batches'
      });
    }

    // Add SMTP Configuration if user has permission
    if (hasPermission('canViewSMTP')) {
      items.push({
        path: '/smtp',
        icon: <FiServer className="sidebar-icon" />,
        text: 'SMTP Configuration'
      });
    }

    return items;
  };

  // Admin menu items (permission-based)
  const getAdminMenuItems = () => {
    const items = [];

    // Add User Dashboard for all authenticated users
    items.push({
      path: '/',
      icon: <FiGrid className="sidebar-icon" />,
      text: 'Dashboard',
      end: true
    });

    // Add Projects only if user has permission
    if (hasPermission('canViewProjects')) {
      items.push({
        path: '/projects',
        icon: <FiFolder className="sidebar-icon" />,
        text: 'Projects'
      });
    }

    // Add Activity only if user has permission
    if (hasPermission('canViewActivity')) {
      items.push({
        path: '/activity',
        icon: <FiActivity className="sidebar-icon" />,
        text: 'Activity'
      });
    }

    // Add Library only if user has permission
    if (hasPermission('canViewLibrary')) {
      items.push({
        path: '/library',
        icon: <FiBookmark className="sidebar-icon" />,
        text: 'Library'
      });
    }

    // Add Invitations only if user has permission to view invitations
    if (hasPermission('canViewInvitations')) {
      items.push({
        path: '/invitations',
        icon: <FiUsers className="sidebar-icon" />,
        text: 'Invitations'
      });
    }

    // Add Team Members only if user has permission to view team members
    if (hasPermission('canViewTeamMembers')) {
      items.push({
        path: '/team-members',
        icon: <FiUserCheck className="sidebar-icon" />,
        text: 'Team Members'
      });
    }

    // Add Settings only if user has permission
    if (hasPermission('canViewSettings')) {
      items.push({
        path: '/settings',
        icon: <FiSettings className="sidebar-icon" />,
        text: 'Settings'
      });
    }

    // Add Email Builder only if user has permission to view templates
    if (hasPermission('canViewTemplates')) {
      items.push({
        path: '/email-builder',
        icon: <FiEdit3 className="sidebar-icon" />,
        text: 'Email Builder'
      });
      items.push({
        path: '/modern-email-builder',
        icon: <FiEdit3 className="sidebar-icon" />,
        text: 'Modern Email Builder'
      });
      items.push({
        path: '/ultra-modern-email-builder',
        icon: <FiGrid className="sidebar-icon" />,
        text: 'Ultra Modern Builder'
      });
      items.push({
        path: '/new-builderjs',
        icon: <FiLayers className="sidebar-icon" />,
        text: 'New BuilderJS'
      });
      items.push({
        path: '/builderjs6',
        icon: <FiEdit3 className="sidebar-icon" />,
        text: 'BuilderJS6'
      });
      items.push({
        path: '/cronjobs',
        icon: <FiClock className="sidebar-icon" />,
        text: 'Scheduled Emails'
      });
    }

    // Add Email Campaigns only if user has permission to view email campaigns
    if (hasPermission('canViewEmailCampaigns')) {
      items.push({
        path: '/email-campaigns',
        icon: <FiMail className="sidebar-icon" />,
        text: 'Email Campaigns'
      });
      items.push({
        path: '/email-batches',
        icon: <FiUsers className="sidebar-icon" />,
        text: 'Email Batches'
      });
    }

    // Add SMTP Configuration if user has permission
    if (hasPermission('canViewSMTP')) {
      items.push({
        path: '/smtp',
        icon: <FiServer className="sidebar-icon" />,
        text: 'SMTP Configuration'
      });
    }

    // Add Role Management only if user has permission to update roles
    if (hasPermission('canUpdateRoles')) {
      items.push({
        path: '/role-management',
        icon: <FiShield className="sidebar-icon" />,
        text: 'Role Management'
      });
    }

    // Add Role Assignment only if user has permission to manage users
    if (hasPermission('canManageUsers')) {
      items.push({
        path: '/user-role-assignment',
        icon: <FiUserCheck className="sidebar-icon" />,
        text: 'Assign Roles'
      });
    }

    // Add CDN for all users (can be used by anyone)
    items.push({
      path: '/cdn',
      icon: <FiImage className="sidebar-icon" />,
      text: 'Image CDN'
    });

    return items;
  };

  // Regular user menu items (permission-based)
  const getRegularMenuItems = () => {
    const items = [];

    // Add User Dashboard for all authenticated users
    items.push({
      path: '/',
      icon: <FiGrid className="sidebar-icon" />,
      text: 'Dashboard',
      end: true
    });

    // Add Projects only if user has permission
    if (hasPermission('canViewProjects')) {
      items.push({
        path: '/projects',
        icon: <FiFolder className="sidebar-icon" />,
        text: 'Projects'
      });
    }

    // Add Activity only if user has permission
    if (hasPermission('canViewActivity')) {
      items.push({
        path: '/activity',
        icon: <FiFileText className="sidebar-icon" />,
        text: 'Activity'
      });
    }

    // Add Library only if user has permission
    if (hasPermission('canViewLibrary')) {
      items.push({
        path: '/library',
        icon: <FiBookmark className="sidebar-icon" />,
        text: 'Library'
      });
    }

    // Add Invitations only if user has permission to view invitations
    if (hasPermission('canViewInvitations')) {
      items.push({
        path: '/invitations',
        icon: <FiUsers className="sidebar-icon" />,
        text: 'Invitations'
      });
    }

    // Add Team Members only if user has permission to view team members
    if (hasPermission('canViewTeamMembers')) {
      items.push({
        path: '/team-members',
        icon: <FiUserCheck className="sidebar-icon" />,
        text: 'Team Members'
      });
    }

    // Add Settings only if user has permission
    if (hasPermission('canViewSettings')) {
      items.push({
        path: '/settings',
        icon: <FiSettings className="sidebar-icon" />,
        text: 'Settings'
      });
    }

    // Add Email Builder only if user has permission to view templates
    if (hasPermission('canViewTemplates')) {
      items.push({
        path: '/email-builder',
        icon: <FiEdit3 className="sidebar-icon" />,
        text: 'Email Builder'
      });
      items.push({
        path: '/modern-email-builder',
        icon: <FiEdit3 className="sidebar-icon" />,
        text: 'Modern Email Builder'
      });
      items.push({
        path: '/ultra-modern-email-builder',
        icon: <FiGrid className="sidebar-icon" />,
        text: 'Ultra Modern Builder'
      });
      items.push({
        path: '/new-builderjs',
        icon: <FiLayers className="sidebar-icon" />,
        text: 'New BuilderJS'
      });
      items.push({
        path: '/builderjs6',
        icon: <FiEdit3 className="sidebar-icon" />,
        text: 'BuilderJS6'
      });
      items.push({
        path: '/cronjobs',
        icon: <FiClock className="sidebar-icon" />,
        text: 'Scheduled Emails'
      });
    }

    // Add Email Campaigns only if user has permission to view email campaigns
    if (hasPermission('canViewEmailCampaigns')) {
      items.push({
        path: '/email-campaigns',
        icon: <FiMail className="sidebar-icon" />,
        text: 'Email Campaigns'
      });
      items.push({
        path: '/email-batches',
        icon: <FiUsers className="sidebar-icon" />,
        text: 'Email Batches'
      });
    }

    // Add Role Management only if user has permission to update roles
    if (hasPermission('canUpdateRoles')) {
      items.push({
        path: '/role-management',
        icon: <FiShield className="sidebar-icon" />,
        text: 'Role Management'
      });
    }

    // Add Role Assignment only if user has permission to manage users
    if (hasPermission('canManageUsers')) {
      items.push({
        path: '/user-role-assignment',
        icon: <FiUserCheck className="sidebar-icon" />,
        text: 'Assign Roles'
      });
    }

    // Add CDN for all users (can be used by anyone)
    items.push({
      path: '/cdn',
      icon: <FiImage className="sidebar-icon" />,
      text: 'Image CDN'
    });

    return items;
  };

  // Choose menu items based on role and permissions
  let menuItems;
  
  // If user has Admin permissions but role check fails, use Admin menu
  const hasAdminPermissions = hasPermission('canManageUsers') && hasPermission('canUpdateRoles');
  
  if (isSuperAdmin) {
    menuItems = getSuperAdminMenuItems();
  } else if (isAdmin || hasAdminPermissions) {
    // Use Admin menu if role is Admin OR if user has admin-level permissions
    menuItems = getAdminMenuItems();
  } else {
    menuItems = getRegularMenuItems();
  }

  // Apply custom menu order if available
  const applyCustomOrder = (items) => {
    if (!customMenuOrder || customMenuOrder.length === 0) {
      return items;
    }

    const orderedItems = [];
    const remainingItems = [...items];
    const usedPaths = new Set();

    // First, add items in custom order (only if they still exist in current menu)
    customMenuOrder.forEach(path => {
      const itemIndex = remainingItems.findIndex(item => item.path === path);
      if (itemIndex !== -1) {
        orderedItems.push(remainingItems[itemIndex]);
        remainingItems.splice(itemIndex, 1);
        usedPaths.add(path);
      }
    });

    // Then add any remaining items that weren't in custom order
    // This ensures new menu items always appear, even if not in saved order
    remainingItems.forEach(item => {
      if (!usedPaths.has(item.path)) {
        orderedItems.push(item);
      }
    });

    return orderedItems;
  };

  // Only apply custom order if preferences are loaded (not loading)
  const orderedMenuItems = loadingPreferences ? menuItems : applyCustomOrder(menuItems);

  // For mobile, limit to most important items
  const getMobileMenuItems = (items) => {
    // For mobile, show only the most important items
    const mobileItems = items.filter(item => 
      ['Projects', 'Activity', 'Library', 'Team Members', 'Settings', 'Email Builder', 'Modern Email Builder', 'Ultra Modern Builder', 'Email Campaigns', 'Scheduled Emails', 'Assign Roles', 'Role Management', 'Admin Panel'].includes(item.text)
    );
    
    // If we have too many items, prioritize the first 6
    return mobileItems.slice(0, 6);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && isMobileMenuOpen && !event.target.closest('.sidebar')) {
        closeMobileMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, isMobileMenuOpen, closeMobileMenu]);

  // Check if user is authenticated
  const isAuthenticated = config.isAuthenticated();
  
  if (!isAuthenticated) {
    return null; // Don't show sidebar if not authenticated
  }

  const displayItems = isMobile ? getMobileMenuItems(orderedMenuItems) : orderedMenuItems;

  // Determine sidebar class based on hover state and screen size
  const getSidebarClass = () => {
    if (isMobile) {
      return `sidebar ${isMobileMenuOpen ? 'sidebar-mobile-open' : ''}`;
    }
    if (isHovered && window.innerWidth <= 900) return 'sidebar sidebar-expanded';
    return 'sidebar';
  };

  const handleMenuOrderSave = async (newMenuItems) => {
    const newOrder = newMenuItems.map(item => item.path);
    
    try {
      // Save to server first (source of truth)
      const userId = config.getUserId();
      if (userId) {
        const response = await axiosInstance.put(
          `${config.api.baseURL}/${config.api.apiPath}/user/menu-preferences/${userId}`,
          { menuOrder: newOrder }
        );
        
        if (response.data.statusCode === 200) {
          // Update local state only after server success
          setCustomMenuOrder(newOrder);
        } else {
          console.error('Failed to save menu preferences to server');
        }
      }
    } catch (error) {
      console.error('Error saving menu preferences:', error);
      // Don't update local state if server save fails
    }
  };

  // Shimmer effect component for menu items
  const MenuShimmer = () => {
    // More realistic menu item configurations
    const shimmerConfigs = [
      { iconSize: 24, textWidth: 85, delay: 0 },
      { iconSize: 22, textWidth: 120, delay: 0.1 },
      { iconSize: 26, textWidth: 70, delay: 0.2 },
      { iconSize: 20, textWidth: 110, delay: 0.3 },
      { iconSize: 24, textWidth: 95, delay: 0.4 },
      { iconSize: 22, textWidth: 80, delay: 0.5 },
      { iconSize: 26, textWidth: 100, delay: 0.6 },
      { iconSize: 20, textWidth: 90, delay: 0.7 }
    ];

    return (
      <>
        {shimmerConfigs.map((config, index) => (
          <li key={index} style={{ animationDelay: `${config.delay}s` }}>
            <div className="menu-shimmer-item">
              <div 
                className="menu-shimmer-icon"
                style={{ 
                  width: `${config.iconSize}px`, 
                  height: `${config.iconSize}px` 
                }}
              ></div>
              <div 
                className="menu-shimmer-text"
                style={{ width: `${config.textWidth}px` }}
              ></div>
            </div>
          </li>
        ))}
      </>
    );
  };

  return (
    <>
      {/* Mobile menu overlay */}
      {isMobile && isMobileMenuOpen && (
        <div className="sidebar-overlay" onClick={closeMobileMenu} />
      )}
      
      <aside 
        className={getSidebarClass()}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
      >
        {/* Mobile menu toggle button */}
        {isMobile && (
          <button 
            className="mobile-menu-toggle"
            onClick={closeMobileMenu}
            aria-label="Close menu"
          >
            <FiX size={24} />
          </button>
        )}
        
        <div className="sidebar-logo">
          <span className="logo-icon-glass">
            {isSuperAdmin ? <FiShield size={24} /> : isAdmin ? <FiUserCheck size={24} /> : <FiMail size={24} />}
          </span>
          <span className="logo-text">
            {isSuperAdmin ? 'MailChimp Admin' : isAdmin ? 'MailChimp Admin' : 'MailChimp'}
          </span>
        </div>

        {/* Menu Order Button - Only show on desktop */}
        {!isMobile && (
          <button
            className="menu-order-btn"
            onClick={() => setShowMenuOrderManager(true)}
            title="Customize Menu Order"
          >
            <FiList size={16} />
            <span>Customize Menu</span>
          </button>
        )}
      
      {/* Add section divider for admin users - only show on desktop */}
      {!isMobile && (isSuperAdmin || isAdmin) && (
        <div style={{
          padding: '0 0.5rem',
          marginBottom: '1rem'
        }}>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: '600',
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '0.5rem',
            padding: '0 0.5rem'
          }}>
            {isSuperAdmin ? 'Super Admin Tools' : 'Admin Tools'}
          </div>
        </div>
      )}
      
      <nav>
        <ul>
          {loadingPreferences ? (
            <MenuShimmer />
          ) : (
            displayItems.map((item, index) => (
              <li key={index}>
                <NavLink 
                  to={item.path} 
                  end={item.end || false}
                  className={({ isActive }) => isActive ? 'active' : ''}
                  title={item.text}
                >
                  {item.icon}
                  <span className="sidebar-link-text">{item.text}</span>
                </NavLink>
              </li>
            ))
          )}
        </ul>
      </nav>
      
      {/* Add user role indicator at bottom - only show on desktop */}
      {!isMobile && (
        <div style={{
          marginTop: 'auto',
          padding: '0.75rem',
          background: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          fontSize: '0.75rem',
          color: '#64748b',
          textAlign: 'center'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
            {userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1) : 'Member'}
          </div>
          <div style={{ fontSize: '0.65rem', opacity: '0.8' }}>
            Role
          </div>
        </div>
      )}

      {/* Menu Order Manager Modal */}
      <MenuOrderManager
        isOpen={showMenuOrderManager}
        onClose={() => setShowMenuOrderManager(false)}
        onSave={handleMenuOrderSave}
        currentMenuItems={orderedMenuItems}
      />
    </aside>
    </>
  );
}

export default RoleBasedSidebar;  